//!#system	

<template>
	<div>
		<v-simple-table v-if="type === 'server'">
			<template v-slot:default>
				<thead>
					<tr>
						<th class="text-left">Helper</th>
						<th class="text-left">Example</th>
						<th class="text-left">Description</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td><code>csvFormatter</code></td>
						<td nowrap><code>{{example("csvFormatter myKey true")}}</code></td>
						<td>Format a string as a CSV value. Double-quotes will be doubled-up, and the string will be wrapped in double-quotes if it contains a comma. The second parameter is optional, and directs the helper to always wrap the string in double quotes. You may want to use 3 curly braces to avoid HTML encoding issues.</td>
					</tr>
					<tr>
						<td><code>crlf</code></td>
						<td nowrap><code>{{example("crlf~")}}</code></td>
						<td>Output a Windows-style ling ending when creating a flat file.</td>
					</tr>
					<tr>
						<td><code>currency</code></td>
						<td nowrap><code>{{example("currency price priceCurrencyCode")}}</code></td>
						<td>Format a currency value with the symbol that corresponds to the currency code that is supplied.</td>
					</tr>
					<tr>
						<td><code>dateFormat</code></td>
						<td nowrap><code>{{example("dateFormat dateKey 'YYYY-MM-DD hh:mm:ss'")}}</code></td>
						<td>Format a date value using Moment.js date formatting strings. Dates will be in local-time, relative to the server.</td>
					</tr>
					<tr>
						<td><code>encodeUrl</code></td>
						<td nowrap><code>{{example("encodeUrl myKey")}}</code></td>
						<td>Encode the value for use in a URL. You may want to use 3 curly braces to avoid HTML encoding issues.</td>
					</tr>
					<tr>
						<td><code>ifeq</code></td>
						<td nowrap><code>{{example("#ifeq myKey 'ABC'")}}Output something{{example("/ifeq")}}</code></td>
						<td>Alternate to <code>{{example('#if ...')}}</code> that checks to see if the first parameter matches the second parameter.</td>
					</tr>
					<tr>
						<td><code>ifinc</code></td>
						<td nowrap><code>{{example("#ifinc myKey 'ABC' 'DEF'")}}Output something{{example("/ifinc")}}</code></td>
						<td>Alternate to <code>{{example('#if ...')}}</code> that checks to see if the first parameter is included in the second and following parameters.</td>
					</tr>
					<tr>
						<td><code>ifnoteq</code></td>
						<td nowrap><code>{{example("#ifnoteq myKey 'ABC'")}}Output something{{example("/ifnoteq")}}</code></td>
						<td>Alternate to <code>{{example('#if ...')}}</code> that checks to see if the first parameter does not match the second parameter.</td>
					</tr>
					<tr>
						<td><code>join</code></td>
						<td nowrap><code>{{example("join tags '-'")}}</code></td>
						<td>Join an array of strings together with an optional delimiter (defaults to a comma). Useful when you want to return the tags on a bucket item in one field.</td>
					</tr>
					<tr>
						<td><code>jsonFormatter</code></td>
						<td nowrap><code>"{{example("jsonFormatter myKey")}}"</code></td>
						<td>Format a string for a JSON payload.  Escapes double-quotes with a backslash.  You still need to wrap this helper in quotes if you want the value to be quoted.</td>
					</tr>
					<tr>
						<td><code>lf</code></td>
						<td nowrap><code>{{example("lf~")}}</code></td>
						<td>Output a Unix/Linux-style ling ending when creating a flat file.</td>
					</tr>
					<tr>
						<td><code>multiply</code></td>
						<td nowrap><code>{{example("multiply total 100.00")}}</code></td>
						<td>Multiply the first parameter by the second parameter.</td>
					</tr>
					<tr>
						<td><code>negative</code></td>
						<td nowrap><code>{{example("negative 100.00")}}</code></td>
						<td>Convert the argument to a negative number.</td>
					</tr>
					<tr>
						<td><code>round</code></td>
						<td nowrap><code>{{example("round total 2")}}</code></td>
						<td>Round the number to a specified number of digits.</td>
					</tr>
					<tr>
						<td><code>sum</code></td>
						<td nowrap><code>{{example("sum myArray")}}</code></td>
						<td>For arrays of numbers, you can sum the numbers and output them.</td>
					</tr>
					<tr>
						<td><code>switch</code></td>
						<td><code>{{example("#switch propertyName")}}{{example("#case 'value'")}}First thing{{example("/case")}}{{example("#case 'value2'")}}Second thing{{example("/case")}}{{example("#default")}}Default{{example("/default")}}{{example("/switch")}}</code></td>
						<td>A switch/case statement for translating a set of values.</td>
					</tr>
					<tr>
						<td><code>tab</code></td>
						<td nowrap><code>{{example("tab")}}</code></td>
						<td>Output a tab (`\t`) character.  This is useful when creating tab delimited files.</td>
					</tr>
					<tr>
						<td><code>tabFormatter</code></td>
						<td nowrap><code>{{example("tabFormatter myKey")}}</code></td>
						<td>Format a string as a TAB value. If the string contains an actual tab character, it will be replaced with a space. You may want to use 3 curly braces to avoid HTML encoding issues.</td>
					</tr>
					<tr>
						<td><code>toNum</code></td>
						<td nowrap><code>{{example("toNum myString")}}</code></td>
						<td>Convert a string to a number.  Non-numeric strings will be 0.</td>
					</tr>
					<tr>
						<td><code>sumOfArrayProperty</code></td>
						<td nowrap><code>{{example("sumOfArrayProperty myArray, 'key'")}}</code></td>
						<td>For arrays of objects, that contain a number, you can sum the numbers and output them. The first parameter is the array, and the second parameter (string) is the object property key that should be summed.</td>
					</tr>
					<tr>
						<td><code>when</code></td>
						<td nowrap><code>{{example("#when myKey 'eq' 'my value'")}} output this {{example("/when")}}</code></td>
						<td>A convenient option for conditional logic with operator support. Operators supported: <code>eq</code>, <code>noteq</code>, <code>startswith</code>, <code>endswith</code>, <code>includes</code>, <code>gt</code>, <code>gteq</code>, <code>gteq</code>, <code>lt</code>, <code>or</code>, <code>and</code>, and <code>%</code>.</td>
					</tr>
					<tr>
						<td><code>xmlFormatter</code></td>
						<td nowrap><code>{{example("xmlFormatter myKey")}}</code></td>
						<td>Format a string for an XML payload.  Escapes any entities with their proper replacements.</td>
					</tr>
				</tbody>
			</template>
		</v-simple-table>
		<v-simple-table v-if="type === 'client'">
			<template v-slot:default>
				<thead>
					<tr>
						<th class="text-left">Helper</th>
						<th class="text-left">Example</th>
						<th class="text-left">Description</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td><code>dateFormat</code></td>
						<td nowrap><code>{{example("dateFormat dateKey 'YYYY-MM-DD hh:mm:ss'")}}</code></td>
						<td>Format a date value using Moment.js date formatting strings. Dates will be in local-time, relative to the server.</td>
					</tr>
					<tr>
						<td><code>comma</code></td>
						<td nowrap><code>{{example("comma")}}</code></td>
						<td>Output a comma (",")</td>
					</tr>
				</tbody>
			</template>
		</v-simple-table>
	</div>
</template>

<script>
export default {
	name: 'HandlebarsDocs',
	props: {
		type: {
			type: String,
			default: 'server'
		}
	},
	data() {
		return {}
	},
	methods: {
		example(str) {
			return '{{' + str + '}}';
		}
	}
}
</script>